import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Tooltip from 'components/Shared/Tooltip/Index'

import * as sessionUserSelector from 'selectors/sessionUser'

import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'
import ToggleSwitch from 'components/Shared/ToggleSwitch/Index'
import FilterBadge from './FilterBadge'


// it should be placed before StyledWrapper
const Buttons = styled.div`
  display: flex;
  opacity: 0;
  transition: opacity 0.3s;
`

const StyledWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  min-height: 40px;
  box-sizing: border-box;
  padding: 12px 8px 12px 0;
  border-bottom: 1px solid ${colors.secondary70};
  background-color: ${props => props.isEditing ? colors.primary95 : colors.secondary98};
  ${Buttons} {
    opacity: ${props => props.isEditing ? 1 : 0};
  }

  &:hover {
    ${Buttons} {
      opacity: 1;
    }
    background-color: ${colors.hover};
  }
`

const ActiveZone = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  padding: 0 8px;
`

const FilterName = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: ${props => props.isActive ? '#000' : colors.secondary40};
  word-break: break-all;
  padding-left: 8px;
`

const EditButton = styled(IconLink)`
  margin-left: 4px;
`

const DeleteButton = styled(IconLink)`
  margin-left: 12px;
`

const TicketFilter = ({ filter, activeTicketFilterIds, onToggle, onEdit, onDelete, isEditing }) => {
  const { t } = useTranslation()
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)

  const isActive = activeTicketFilterIds.includes(filter.id)

  const onToggle_ = (e) => {
    // to prevent processing click event twice
    e.preventDefault()
    onToggle(filter, !isActive)
  }

  const editFilter = (event) => {
    onEdit(filter)
  }

  const deleteFilter = (event) => {
    onDelete(filter)
  }

  const isGlobal = filter.global
  const isPublished = filter.published
  let tooltip = t(isPublished ? 'ticketsPage.tooltipMultiUserFilter' : 'ticketsPage.tooltipAdminOnlyFilter')
  if (!isGlobal) {
    tooltip = t('ticketsPage.tooltipPrivateFilter')
  }
  const tooltipId = `filter-tooltip-${filter.id}`
  const canModify = Boolean(userIsAdmin && !isGlobal) || !isGlobal

  return (
    <StyledWrapper isEditing={isEditing} data-test='custom-filter'>
      <ActiveZone onClick={onToggle_}>
        <ToggleSwitch
          value={isActive}
        />
        <FilterBadge
          isGlobal={isGlobal}
          isPublished={filter.published}
          tooltipText={tooltip}
          tooltipId={tooltipId}
        />
        <FilterName isActive={isActive}>
          {filter.name}
        </FilterName>
      </ActiveZone>
      <Tooltip id={tooltipId} />

      {canModify && (
        <Buttons>
          <EditButton
            onClick={editFilter}
            iconName='icon-edit-16'
            active={isEditing}
            data-tooltip-content={t('ticketsPage.editFilterTooltip')}
            data-tooltip-id={tooltipId}
          />
          <DeleteButton
            onClick={deleteFilter}
            iconName='icon-remove-16'
            data-tooltip-content={t('ticketsPage.removeFilterTooltip')}
            data-tooltip-id={tooltipId}
          />
        </Buttons>
      )}
    </StyledWrapper>
  )
}

TicketFilter.propTypes = {
  filter: PropTypes.object.isRequired,
  activeTicketFilterIds: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
}

export default TicketFilter
